import { Controller } from "@hotwired/stimulus"

// 完成ドキュメントのセクション編集・AI再生成。
// 編集: テキストエリアにトグル → PATCH保存 → リロードで反映
// 再生成: POST → リロードで反映（失敗時は現状維持）
export default class extends Controller {
  static targets = ["section", "text", "job", "jobDisplay", "jobForm"]
  static values = { updateUrl: String, regenerateUrl: String, updateJobUrl: String }

  edit(event) {
    const section = event.currentTarget.dataset.section
    const wrapper = this.sectionTargets.find((el) => el.dataset.section === section)
    const textEl = this.textTargets.find((el) => el.dataset.section === section)
    if (!wrapper || !textEl || wrapper.querySelector("textarea")) return

    // 現在値を取得（skillsは1行1項目に変換）
    const currentValue = section === "skills"
      ? Array.from(textEl.querySelectorAll("li")).map((li) => li.textContent.replace(/^◆?\s*/, "").trim()).join("\n")
      : textEl.textContent.trim()

    textEl.classList.add("hidden")

    const editor = document.createElement("div")
    editor.className = "kk-noprint"
    editor.innerHTML = `
      <textarea rows="${section === "self_pr" ? 6 : 5}"
                class="w-full resize-y rounded-lg border-[1.5px] border-kk-line p-3 font-kk-doc text-sm leading-relaxed text-kk-ink focus:border-kk-navy focus:ring-2 focus:ring-kk-navy-soft"></textarea>
      ${section === "skills" ? '<p class="mt-1 text-[11px] text-kk-sub">1行に1項目で入力してください</p>' : ""}
      <div class="mt-2 flex gap-2">
        <button type="button" data-role="save"
                class="cursor-pointer rounded-lg bg-kk-navy px-3 py-1.5 text-xs font-bold text-white">✓ 保存</button>
        <button type="button" data-role="cancel"
                class="cursor-pointer rounded-lg border border-kk-line bg-white px-3 py-1.5 text-xs text-kk-sub">取消</button>
      </div>`
    const textarea = editor.querySelector("textarea")
    textarea.value = currentValue

    editor.querySelector('[data-role="save"]').addEventListener("click", () => this.save(section, textarea.value))
    editor.querySelector('[data-role="cancel"]').addEventListener("click", () => {
      editor.remove()
      textEl.classList.remove("hidden")
    })

    wrapper.appendChild(editor)
    textarea.focus()
  }

  async save(section, value) {
    const response = await fetch(this.updateUrlValue, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfToken() },
      body: JSON.stringify({ section, value })
    })
    if (response.ok) {
      window.Turbo ? window.Turbo.visit(window.location.href, { action: "replace" }) : window.location.reload()
    } else {
      alert("保存に失敗しました。もう一度お試しください。")
    }
  }

  // 「AIで再生成」: 追加の指示（任意）を入力できるパネルを開く
  regenerate(event) {
    const section = event.currentTarget.dataset.section
    const wrapper = this.sectionTargets.find((el) => el.dataset.section === section)
    if (!wrapper || wrapper.querySelector('[data-role="regen-panel"]')) return

    const panel = document.createElement("div")
    panel.className = "kk-noprint mt-2 rounded-lg border border-kk-line bg-kk-surface p-3"
    panel.dataset.role = "regen-panel"
    panel.innerHTML = `
      <p class="mb-1.5 text-[11px] font-medium text-kk-navy-deep">AIへの追加の指示（任意）</p>
      <textarea rows="2" maxlength="500"
                placeholder="例：もっと簡潔に／数字や実績を強調して／マネジメント経験を前面に"
                class="w-full resize-y rounded-lg border-[1.5px] border-kk-line bg-white p-2.5 font-kk-ui text-sm leading-relaxed text-kk-ink focus:border-kk-navy focus:ring-2 focus:ring-kk-navy-soft"></textarea>
      <div class="mt-2 flex gap-2">
        <button type="button" data-role="run"
                class="cursor-pointer rounded-lg bg-kk-navy px-3 py-1.5 text-xs font-bold text-white">✨ 再生成する</button>
        <button type="button" data-role="cancel"
                class="cursor-pointer rounded-lg border border-kk-line bg-white px-3 py-1.5 text-xs text-kk-sub">取消</button>
      </div>`

    const textarea = panel.querySelector("textarea")
    const runButton = panel.querySelector('[data-role="run"]')
    runButton.addEventListener("click", () => this.runRegenerate(section, textarea.value.trim(), runButton))
    panel.querySelector('[data-role="cancel"]').addEventListener("click", () => panel.remove())

    wrapper.appendChild(panel)
    textarea.focus()
  }

  async runRegenerate(section, instruction, button) {
    button.disabled = true
    button.textContent = "再生成中…"

    try {
      const response = await fetch(this.regenerateUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify({ section, instruction })
      })
      if (!response.ok) throw new Error()
      window.Turbo ? window.Turbo.visit(window.location.href, { action: "replace" }) : window.location.reload()
    } catch {
      button.disabled = false
      button.textContent = "✨ 再生成する"
      alert("再生成に失敗しました。現在の内容を維持します。")
    }
  }

  // 職務経歴（1社分）の編集フォームを開く
  editJob(event) {
    const wrapper = this.findJob(event.currentTarget.dataset.jobIndex)
    if (!wrapper) return
    this.jobDisplayTargets.find((el) => wrapper.contains(el))?.classList.add("hidden")
    this.jobFormTargets.find((el) => wrapper.contains(el))?.classList.remove("hidden")
  }

  // 編集フォームを閉じて表示に戻す（変更は破棄し、保存済みの初期値へ戻す）
  cancelJob(event) {
    const wrapper = this.findJob(event.currentTarget.dataset.jobIndex)
    if (!wrapper) return
    const form = this.jobFormTargets.find((el) => wrapper.contains(el))
    if (form) {
      if (typeof form.reset === "function") form.reset()
      // reset後の「在籍中」状態に合わせて終了年月の表示を戻す
      const isCurrent = form.querySelector('[data-field="current"]')?.checked
      form.querySelector('[data-field="endSelects"]')?.classList.toggle("hidden", isCurrent)
      form.classList.add("hidden")
    }
    this.jobDisplayTargets.find((el) => wrapper.contains(el))?.classList.remove("hidden")
  }

  // 「在籍中」チェックで在籍終了の年月セレクトを表示／非表示する
  toggleJobCurrent(event) {
    const form = event.currentTarget.closest('[data-resume-document-target="jobForm"]')
    form?.querySelector('[data-field="endSelects"]')?.classList.toggle("hidden", event.currentTarget.checked)
  }

  // 編集フォームの内容を職務経歴として保存する
  async saveJob(event) {
    const index = Number(event.currentTarget.dataset.jobIndex)
    const wrapper = this.findJob(index)
    const form = this.jobFormTargets.find((el) => wrapper.contains(el))
    if (!form) return
    const field = (name) => form.querySelector(`[data-field="${name}"]`)
    const isCurrent = field("current").checked

    const job = {
      company: field("company").value.trim(),
      employment: field("employment").value,
      business: field("business").value.trim(),
      duties: field("duties").value,
      achievements: field("achievements").value.trim(),
      period: {
        start: `${field("startYear").value}-${field("startMonth").value}`,
        end: isCurrent ? "" : `${field("endYear").value}-${field("endMonth").value}`,
        current: isCurrent
      }
    }

    const response = await fetch(this.updateJobUrlValue, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfToken() },
      body: JSON.stringify({ job_index: index, job })
    })
    if (response.ok) {
      window.Turbo ? window.Turbo.visit(window.location.href, { action: "replace" }) : window.location.reload()
    } else {
      alert("保存に失敗しました。もう一度お試しください。")
    }
  }

  findJob(index) {
    return this.jobTargets.find((el) => el.dataset.jobIndex === String(index))
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
